import { TYPE_LABEL, PRICE_LABEL, PRICE_WORD, PRICE } from './config.js'
import { quartierOf, isSaved, toggleSaved, lockedCount } from './data.js'

const modal = () => document.getElementById('modal')
const backdrop = () => document.getElementById('modal-backdrop')
let onSaveChange = () => {}
let keyHandlers = {}

export function initModal({ onSave } = {}) {
  onSaveChange = onSave || (() => {})
  backdrop().onclick = closeModal
  document.addEventListener('keydown', (e) => {
    if (lightboxOpen) {
      if (e.key === 'Escape') closeLightbox()
      else if (e.key === 'ArrowRight') lbStep(1)
      else if (e.key === 'ArrowLeft') lbStep(-1)
      return
    }
    if (modal().hidden) return
    if (e.key === 'Escape') closeModal()
    else if (e.key === 'ArrowRight') galleryStep(1)
    else if (e.key === 'ArrowLeft') galleryStep(-1)
  })
}

// ---- detail modal with gallery ----
let gallery = { photos: [], i: 0 }

export function openDetail(p) {
  const photos = (p.photos && p.photos.length ? p.photos : (p.image ? [p.image] : []))
  gallery = { photos, i: 0 }
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((p.name || '') + ' Paris')}`

  // Quick-facts pills — scannable, so the modal isn't a wall of text.
  const facts = []
  if (p.cuisine) facts.push(['cuisine', cap(p.cuisine)])
  facts.push(['type', TYPE_LABEL[p.type]?.replace(/s$/, '') || p.type])
  if (p.price) facts.push(['price', `${PRICE_LABEL[p.price]} · ${PRICE_WORD[p.price]}`])
  if (p.michelin) facts.push(['michelin', `${'★'.repeat(p.michelin)} Michelin`])
  const q = quartierOf(p)
  if (q) facts.push(['hood', q])
  const factsRow = `<div class="facts">${facts.map(([k, v]) =>
    `<span class="fact fact-${k}">${esc(v)}</span>`).join('')}</div>`

  // Address line (links out to maps).
  const addr = p.address
    ? `<a class="addr" href="${maps}" target="_blank" rel="noopener">${pinSvg}<span>${esc(p.address)}</span></a>` : ''

  // Break a long single blurb into a lead sentence + body paragraphs.
  const longParas = (p.long && p.long.length) ? p.long : splitProse(p.blurb)
  const lead = longParas[0] ? `<p class="lead-para">${esc(longParas[0])}</p>` : ''
  const rest = longParas.slice(1).map((t) => `<p>${esc(t)}</p>`).join('')

  const know = p.know && p.know.length
    ? `<div class="know"><h4>What to know before you visit</h4><ul>${p.know.map((k) => `<li>${esc(k)}</li>`).join('')}</ul></div>` : ''
  const tags = p.tags && p.tags.length
    ? `<div class="tag-row">${p.tags.map((t) => `<span class="tag-pill">${esc(t)}</span>`).join('')}</div>` : ''
  const review = p.article_url
    ? `<a class="btn-secondary" href="${p.article_url}" target="_blank" rel="noopener">Full review ↗</a>` : ''

  show(`
    <button class="close" aria-label="Close">×</button>
    <div class="gallery">
      ${photos.length ? `<img class="lead" src="${photos[0]}" alt="${esc(p.name)}" />
        <span class="lead-zoom" aria-hidden="true">${expandSvg}</span>` : ''}
      ${photos.length > 1 ? `<div class="thumbs">${photos.map((src, i) =>
        `<img src="${src}" data-i="${i}" class="${i === 0 ? 'active' : ''}" alt="" loading="lazy" />`).join('')}</div>` : ''}
    </div>
    <div class="body">
      <h2 class="serif">${esc(p.name)}</h2>
      ${factsRow}
      ${addr}
      <div class="long">${lead}${rest}</div>
      ${know}
      ${tags}
      <div class="actions">
        <a class="btn-primary" href="${maps}" target="_blank" rel="noopener">${pinSvgSm} Directions</a>
        ${review}
        <button class="btn-icon save ${isSaved(p.id) ? 'saved' : ''}" id="modal-save" aria-label="Save">
          ${heartSvg}
        </button>
        <button class="btn-icon" id="modal-share" aria-label="Share">${shareSvg}</button>
      </div>
    </div>`)

  modal().querySelector('.close').onclick = closeModal
  document.getElementById('modal-share').onclick = (e) => sharePlace(p, e.currentTarget)
  const leadImg = modal().querySelector('.gallery .lead')
  if (leadImg) leadImg.onclick = () => openLightbox(gallery.i)
  modal().querySelector('.lead-zoom')?.addEventListener('click', () => openLightbox(gallery.i))
  modal().querySelectorAll('.thumbs img').forEach((t) => {
    t.onclick = () => setLead(Number(t.dataset.i))
  })
  const saveBtn = document.getElementById('modal-save')
  saveBtn.onclick = () => {
    const s = toggleSaved(p.id)
    saveBtn.classList.toggle('saved', s)
    onSaveChange()
  }
}

// Split a single run-on blurb into a lead sentence + ~2-sentence paragraphs,
// so a long extracted blurb reads as structured prose, not a wall of text.
function splitProse(text) {
  if (!text) return []
  const clean = text.replace(/\s+/g, ' ').trim()
  // Split only at real sentence boundaries: terminator + space + a capital or
  // opening quote/paren. Avoids breaking inside "(…months). The…" parentheticals.
  const sentences = clean.split(/(?<=[.!?])\s+(?=[A-Z"'“(])/)
  if (sentences.length <= 1) return [clean]
  const out = [sentences[0].trim()]
  for (let i = 1; i < sentences.length; i += 2) {
    out.push(sentences.slice(i, i + 2).join(' ').trim())
  }
  return out.filter(Boolean)
}

function cap(s) { return s ? s[0].toUpperCase() + s.slice(1) : s }

const pinSvg = `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="1.8" d="M12 21s7-6 7-11a7 7 0 1 0-14 0c0 5 7 11 7 11Z"/><circle cx="12" cy="10" r="2.4" fill="currentColor"/></svg>`
const pinSvgSm = `<svg viewBox="0 0 24 24" width="15" height="15" style="vertical-align:-2px" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" d="M12 21s7-6 7-11a7 7 0 1 0-14 0c0 5 7 11 7 11Z"/></svg>`
const heartSvg = `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path stroke-linejoin="round" d="M12 20.6l-1.6-1.46C4.95 14.2 2 11.55 2 8.5 2 5.96 3.96 4 6.5 4c1.4 0 2.79.66 3.7 1.71L12 7.3l1.8-1.59C14.71 4.66 16.1 4 17.5 4 20.04 4 22 5.96 22 8.5c0 3.05-2.95 5.7-8.4 10.65L12 20.6Z"/></svg>`
const shareSvg = `<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="2.6"/><circle cx="6" cy="12" r="2.6"/><circle cx="18" cy="19" r="2.6"/><path d="m8.3 10.7 7.4-4.4M8.3 13.3l7.4 4.4"/></svg>`
const expandSvg = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>`

// Share a spot via a ?place=<id> deep link — native share sheet on mobile,
// clipboard copy on desktop. Every share lands a prospect on the map (or paywall).
function sharePlace(p, btn) {
  const url = `${location.origin}${location.pathname}?place=${encodeURIComponent(p.id)}`
  const data = { title: `${p.name} — Paris Eater`, text: `${p.name}, on the Paris Eater map`, url }
  if (navigator.share) {
    navigator.share(data).catch(() => {})
  } else if (navigator.clipboard) {
    navigator.clipboard.writeText(url).then(() => {
      if (!btn) return
      btn.classList.add('copied')
      const t = btn.getAttribute('aria-label')
      btn.setAttribute('aria-label', 'Link copied')
      setTimeout(() => { btn.classList.remove('copied'); btn.setAttribute('aria-label', t) }, 1600)
    })
  }
}

function setLead(i) {
  if (!gallery.photos[i]) return
  gallery.i = i
  modal().querySelector('.lead').src = gallery.photos[i]
  modal().querySelectorAll('.thumbs img').forEach((t) =>
    t.classList.toggle('active', Number(t.dataset.i) === i))
}
function galleryStep(d) {
  if (gallery.photos.length < 2) return
  setLead((gallery.i + d + gallery.photos.length) % gallery.photos.length)
}

// ---- fullscreen lightbox (shows photos at their original aspect ratio) ----
let lightboxOpen = false

function lightboxEl() {
  let lb = document.getElementById('lightbox')
  if (lb) return lb
  lb = document.createElement('div')
  lb.id = 'lightbox'
  lb.hidden = true
  lb.innerHTML = `
    <button class="lb-close" aria-label="Close">×</button>
    <button class="lb-nav lb-prev" aria-label="Previous photo">‹</button>
    <img class="lb-img" alt="" />
    <button class="lb-nav lb-next" aria-label="Next photo">›</button>
    <div class="lb-count"></div>`
  document.body.appendChild(lb)
  lb.querySelector('.lb-close').onclick = closeLightbox
  lb.querySelector('.lb-prev').onclick = (e) => { e.stopPropagation(); lbStep(-1) }
  lb.querySelector('.lb-next').onclick = (e) => { e.stopPropagation(); lbStep(1) }
  lb.onclick = (e) => { if (e.target === lb) closeLightbox() }
  return lb
}

function openLightbox(i) {
  if (!gallery.photos.length) return
  const lb = lightboxEl()
  gallery.i = i
  renderLightbox()
  lb.hidden = false
  lightboxOpen = true
  requestAnimationFrame(() => lb.classList.add('show'))
}

function renderLightbox() {
  const lb = lightboxEl()
  lb.querySelector('.lb-img').src = gallery.photos[gallery.i]
  const multi = gallery.photos.length > 1
  lb.querySelector('.lb-prev').hidden = !multi
  lb.querySelector('.lb-next').hidden = !multi
  lb.querySelector('.lb-count').textContent = multi ? `${gallery.i + 1} / ${gallery.photos.length}` : ''
}

function lbStep(d) {
  gallery.i = (gallery.i + d + gallery.photos.length) % gallery.photos.length
  renderLightbox()
  setLead(gallery.i) // keep the modal gallery in sync underneath
}

function closeLightbox() {
  const lb = document.getElementById('lightbox')
  if (!lb) return
  lb.classList.remove('show')
  lightboxOpen = false
  setTimeout(() => { lb.hidden = true }, 200)
}

// ---- paywall ----
export function openPaywall({ onBuy, onRestore, nearby = 0 } = {}) {
  const intro = nearby > 0
    ? `There ${nearby === 1 ? 'is' : 'are'} <b>${nearby} locked spot${nearby === 1 ? '' : 's'}</b>
       within a short walk of you, part of <b>${lockedCount()}</b> in the full guide. Unlock the
       complete map to see every restaurant, bar, café and bakery I've reviewed.`
    : `This is one of <b>${lockedCount()}</b> spots in the full guide. Unlock the
       complete map to see every restaurant, bar, café and bakery I've reviewed.`
  show(`
    <button class="close" aria-label="Close">×</button>
    <div class="body paywall-body">
      <div class="padlock">🔒</div>
      <h2 class="serif">A locked spot</h2>
      <p>${intro}</p>
      <ul class="benefits">
        <li>All 150+ hand-reviewed spots</li>
        <li>Search, filter by quartier, price &amp; type</li>
        <li>Photos, full reviews &amp; directions for every place</li>
        <li>Save your own shortlist — your custom Paris map</li>
        <li>One-time ${PRICE} — yours forever, no subscription</li>
      </ul>
      <button class="btn-primary" id="pw-buy">Unlock the full map · ${PRICE}</button>
      <button class="linkbtn" id="pw-restore">Already bought? Restore access</button>
    </div>`, 'paywall')
  modal().querySelector('.close').onclick = closeModal
  document.getElementById('pw-buy').onclick = onBuy
  document.getElementById('pw-restore').onclick = onRestore
}

// Shown right after payment while we confirm the transaction with Paddle (its
// status settles a moment after checkout completes).
export function showUnlocking() {
  show(`
    <div class="body paywall-body">
      <div class="spinner" aria-hidden="true"></div>
      <h2 class="serif">Unlocking your map…</h2>
      <p>Confirming your payment with Paddle. This only takes a moment.</p>
    </div>`, 'paywall')
}

// If the unlock can't be confirmed in time, let them retry or fall back to email.
export function showUnlockError(onRetry, onRestore) {
  show(`
    <button class="close" aria-label="Close">×</button>
    <div class="body paywall-body">
      <h2 class="serif">Almost there</h2>
      <p>Your payment went through, but we couldn't confirm the unlock just yet.
      Give it a moment and try again, or we'll email you a one-tap link.</p>
      <button class="btn-primary" id="unlock-retry">Try again</button>
      <button class="linkbtn" id="unlock-restore">Email me a link instead</button>
    </div>`, 'paywall')
  modal().querySelector('.close').onclick = closeModal
  document.getElementById('unlock-retry').onclick = onRetry
  document.getElementById('unlock-restore').onclick = onRestore
}

// Surfaces Paddle's checkout error reason on-screen (for diagnosing live setup).
export function showCheckoutError(detail, onRetry) {
  show(`
    <button class="close" aria-label="Close">×</button>
    <div class="body paywall-body">
      <h2 class="serif">Checkout problem</h2>
      <p>The payment window couldn't open. Reason:</p>
      <p style="word-break:break-word;background:var(--cream);padding:10px 12px;border-radius:8px;font-size:13px;">${esc(detail)}</p>
      <button class="btn-primary" id="ck-retry">Try again</button>
    </div>`, 'paywall')
  modal().querySelector('.close').onclick = closeModal
  document.getElementById('ck-retry').onclick = onRetry
}

// Restore on a new device — buyer enters the email they paid with and we send a
// one-tap magic link (no password, no key to copy).
export function openRestore(onSubmit) {
  show(`
    <button class="close" aria-label="Close">×</button>
    <div class="body paywall-body">
      <h2 class="serif">Restore your map</h2>
      <p>Already bought? Enter the email you used and I'll send a one-tap link to unlock the map on this device.</p>
      <input id="restore-email" class="key-input" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" />
      <div class="key-error" id="key-error" hidden></div>
      <button class="btn-primary" id="restore-submit">Email me a link</button>
    </div>`, 'paywall')
  modal().querySelector('.close').onclick = closeModal
  const input = document.getElementById('restore-email')
  const submit = () => onSubmit?.(input.value.trim())
  input.focus()
  input.onkeydown = (e) => { if (e.key === 'Enter') submit() }
  document.getElementById('restore-submit').onclick = submit
}

export function showRestoreError(msg) {
  const err = document.getElementById('key-error')
  if (!err) return
  err.hidden = !msg
  err.textContent = msg || ''
}

// Confirmation after a restore link is requested (shown regardless, so we never
// reveal whether the email has purchased).
export function showRestoreSent(email) {
  show(`
    <button class="close" aria-label="Close">×</button>
    <div class="body paywall-body">
      <div class="padlock">✉️</div>
      <h2 class="serif">Check your inbox</h2>
      <p>If <b>${esc(email)}</b> bought the map, a one-tap unlock link is on its way. It works once and expires in 30 minutes.</p>
      <button class="btn-primary" id="restore-done">Done</button>
    </div>`, 'paywall')
  modal().querySelector('.close').onclick = closeModal
  document.getElementById('restore-done').onclick = closeModal
}

// ---- shared ----
function show(html, cls = '') {
  const m = modal()
  m.className = `modal ${cls}`
  m.innerHTML = html
  m.hidden = false
  backdrop().hidden = false
  requestAnimationFrame(() => { m.classList.add('show'); backdrop().classList.add('show') })
}

export function closeModal() {
  if (lightboxOpen) closeLightbox()
  const m = modal()
  m.classList.remove('show')
  backdrop().classList.remove('show')
  setTimeout(() => { m.hidden = true; backdrop().hidden = true }, 200)
}

function esc(s = '') {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}
